import React, { useMemo, useRef, useState, useEffect } from "react";
import { Button, Segment, Header } from "semantic-ui-react";
import "./FormSection.scss";
import { useSelector } from "react-redux";
import networkSelectors from "../../../selectors/network";
import {
  deletePermitContact,
  postPermitContactActive,
  postPermitContactConnectUser,
  postPermitContactDisconnectUser,
  postPermitContactInactive,
} from "../../../queries/permits";
import { useParams, useLocation } from "react-router-dom";
import { PermitsContact } from "../types/PermitsContact";
import { ConfirmationModal } from "../../../components/global_components/ConfirmationModal";
import useApiCallWithToast from "../../../hooks/useApiCallWithToast";
import { ContactInfoOptions } from "../types/ContactInfoOptions";
import { ContactSearchModal } from "../../contacts/components/ContactSearchModal";
import { useQueryCache } from "react-query";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import "./ContactEllipsesButtonDropdown.scss";
import {
  faArrowRightArrowLeft,
  faPenToSquare,
  faTrashCan,
  faUserMinus,
  faUserPlus,
} from "@fortawesome/pro-regular-svg-icons";
import { faLinkSlash } from "@fortawesome/pro-solid-svg-icons";
import EditInfoContactSidebar from "./staff/review/contact_information/EditInfoContactSidebar";

const EllipsesButtonOption = ({ icon, header, description, handleClick }: any) => {
  return (
    <Segment
      basic
      onClick={handleClick}
      className={description ? "contacts-advanced-dropdown-option" : "disabled-dropdown-option"}
    >
      <Segment className='icon-wrapper'>{icon}</Segment>
      <div>
        <Header as='h5' content={header} />
        <Header.Subheader content={description} />
      </div>
    </Segment>
  );
};

interface IContactEllipsesButtonDropdown {
  contact: PermitsContact;
  contactInfoOptions: ContactInfoOptions;
  customFieldType?: number; // required if creating a new contact
  permitsExtensionDefId: number | null;
  disableDeleteButton?: boolean;
  contactDoesntExist?: boolean;
  customFieldName?: string;
}

// ellipses dropdown menu that shows different options for editing a contact
const ContactEllipsesButtonDropdown = (props: IContactEllipsesButtonDropdown) => {
  const {
    contact,
    contactInfoOptions,
    customFieldType,
    permitsExtensionDefId,
    disableDeleteButton = true,
    contactDoesntExist = false,
    customFieldName,
  } = props;

  const queryCache = useQueryCache();

  const contactId = useMemo(() => contact?.id, [contact]);
  const { id: permitId } = useParams<{ id: string }>();
  const [showEditContactSidebar, setShowEditContactSidebar] = useState(false);
  const [showDropdownOptions, setShowDropdownOptions] = useState(false);
  const networkHeaders = useSelector(networkSelectors.defaultHeaders);
  const [showDeleteContactModal, setShowDeleteContactModal] = useState(false);
  const [isSearchApplicantModalOpen, setIsSearchApplicantModalOpen] = useState(false);
  const contactType = contact?.contactType;
  const isApplicant = contact?.isApplicant;
  const location = useLocation();
  const onContactsTab = location.hash === "#contacts";

  // mutation to delete contact
  const { run: removePermitContact, loading: isDeletePermitContactLoading } = useApiCallWithToast({
    apiCall: () => deletePermitContact(networkHeaders, contactId),
    onSuccess: () => {
      queryCache.invalidateQueries(["conversation", permitId.toString()]);
      setShowDeleteContactModal(false);
    },
    queryKey: "permits",
    successMessage: `${contactType || "Contact Information"} removed successfully`,
    errorMessage: `We ran into an issue removing the ${contactType || "Contact Information"}`,
  });

  // mutation to mark contact as inactive
  const { run: markContactInactive, loading: isMarkContactInactiveLoading } = useApiCallWithToast({
    apiCall: () => postPermitContactInactive(networkHeaders, permitId, contactId),
    onSuccess: () => {
      queryCache.invalidateQueries(["conversation", permitId.toString()]);
    },
    queryKey: "permits",
    successMessage: "Contact set to inactive",
  });

  // mutation to mark contact as active
  const { run: markContactActive, loading: isMarkContactActiveLoading } = useApiCallWithToast({
    apiCall: () => postPermitContactActive(networkHeaders, permitId, contactId),
    onSuccess: () => {
      queryCache.invalidateQueries(["conversation", permitId.toString()]);
    },
    queryKey: "permits",
    successMessage: "Contact set to active",
  });

  // mutation to disconnect profile
  const { run: disconnectUser, loading: isDisconnectUserLoading } = useApiCallWithToast({
    apiCall: () => postPermitContactDisconnectUser(networkHeaders, permitId, contactId),
    onSuccess: () => {
      queryCache.invalidateQueries(["conversation", permitId.toString()]);
    },
    queryKey: "permits",
    successMessage: "Profile disconnected successfully",
  });

  // mutation to connect profile
  const { run: connectUserMutation, loading: isConnectProfileLoading } = useApiCallWithToast({
    apiCall: () => postPermitContactConnectUser(networkHeaders, permitId, contactId),
    onSuccess: () => {
      queryCache.invalidateQueries(["conversation", permitId.toString()]);
    },
    queryKey: "permits",
    successMessage: "Profile connected successfully",
    useResponseErrorMessage: true,
  });

  const popupRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (popupRef.current && !popupRef.current.contains(event.target as Node)) {
        setShowDropdownOptions(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, []);

  return (
    <>
      {/* modal to delete contact */}
      <ConfirmationModal
        headerText={`Remove ${contactType || "Contact Information"}?`}
        modalText={`This will clear all ${contactType || "relevant"} fields from the application`}
        confirmationButtonColor='red'
        confirmationButtonText='Remove'
        isOpen={showDeleteContactModal}
        closeModal={() => setShowDeleteContactModal(false)}
        closeOnConfirmation={false}
        onConfirmation={() => {
          removePermitContact();
        }}
        isLoading={isDeletePermitContactLoading}
      />
      {/* modal for creating/finding contact to connect profile */}
      <ContactSearchModal
        open={isSearchApplicantModalOpen}
        setModalClosed={() => setIsSearchApplicantModalOpen(false)}
        headerText={`Change ${contactType || "Connected Profile"}`}
        createContactButtonText='Create and Connect Contact'
        selectContactButtonText='Connect to Existing Contact'
        onCreateContact={(newContactId: string) => {
          connectUserMutation({ userIdToConnect: Number(newContactId) });
        }}
        onSelectContact={(contactId: string) => {
          connectUserMutation({ userIdToConnect: Number(contactId) });
        }}
        contactInfoOptions={contactInfoOptions}
      />
      {/* sidebar for editing contact */}
      <EditInfoContactSidebar
        contact={contact}
        showEditContactSidebar={showEditContactSidebar}
        setShowEditContactSidebar={setShowEditContactSidebar}
        customFieldType={customFieldType}
        contactInfoOptions={contactInfoOptions}
        contactDoesntExist={contactDoesntExist}
        customFieldName={customFieldName}
        permitsExtensionDefId={permitsExtensionDefId}
      />
      {/* ellipses button */}
      <Button
        loading={
          isMarkContactInactiveLoading ||
          isMarkContactActiveLoading ||
          isDisconnectUserLoading ||
          isConnectProfileLoading
        }
        secondary
        className='tw-mr-2'
        size='small'
        icon='ellipsis horizontal'
        onClick={() => setShowDropdownOptions(true)}
      />
      {showDropdownOptions && (
        <div
          ref={popupRef}
          className={onContactsTab ? "contacts-tab-advanced-dropdown-options" : "details-tab-advanced-dropdown-options"}
        >
          <Segment ref={popupRef} onClick={() => setShowDropdownOptions(false)}>
            {contactDoesntExist ? (
              <EllipsesButtonOption
                icon={<FontAwesomeIcon icon={faUserPlus} />}
                header={`Add contact information`}
                description={`Add new contact information for this role`}
                handleClick={() => setShowEditContactSidebar(true)}
              />
            ) : !contact.isActive ? (
              <>
                {onContactsTab && (
                  <EllipsesButtonOption
                    icon={<FontAwesomeIcon icon={faUserPlus} />}
                    header='Mark Active'
                    description={`Mark the ${contactType || "contact"} as active`}
                    handleClick={() => markContactActive()}
                  />
                )}
                <EllipsesButtonOption
                  icon={<FontAwesomeIcon size='lg' icon={faTrashCan} />}
                  header={disableDeleteButton ? "Cannot remove a required contact" : "Remove"}
                  description={
                    disableDeleteButton ? null : `Remove the ${contactType || "contact"} from the application`
                  }
                  handleClick={() => (disableDeleteButton ? "" : setShowDeleteContactModal(true))}
                />
              </>
            ) : (
              <>
                <EllipsesButtonOption
                  icon={<FontAwesomeIcon size='lg' icon={faPenToSquare} />}
                  header='Edit Submitted Info'
                  description='Edit only what was submitted on the application'
                  handleClick={() => setShowEditContactSidebar(true)}
                />
                {contact.userId && onContactsTab && (
                  <EllipsesButtonOption
                    icon={<FontAwesomeIcon size='lg' icon={faArrowRightArrowLeft} />}
                    header={`Change ${contactType || "Connected Profile"}`}
                    description={`Change which profile is connected to the ${contactType || "contact information"}`}
                    handleClick={() => setIsSearchApplicantModalOpen(true)}
                  />
                )}
                {contact.userId && onContactsTab && (
                  <EllipsesButtonOption
                    icon={<FontAwesomeIcon size='lg' icon={faLinkSlash} />}
                    header={`Disconnect ${contactType || "Connected Profile"}`}
                    description={`Disconnect the current connected profile from the ${
                      contactType || "contact information"
                    }`}
                    handleClick={() => disconnectUser()}
                  />
                )}
                {onContactsTab && !isApplicant && (
                  <EllipsesButtonOption
                    icon={<FontAwesomeIcon size='lg' icon={faUserMinus} />}
                    header='Mark Inactive'
                    description={`Mark the ${contactType || "contact"} as inactive`}
                    handleClick={() => markContactInactive()}
                  />
                )}
                <EllipsesButtonOption
                  icon={<FontAwesomeIcon size='lg' icon={faTrashCan} />}
                  header={disableDeleteButton ? "Cannot remove a required contact" : "Remove"}
                  description={
                    disableDeleteButton ? null : `Remove the ${contactType || "contact"} from the application`
                  }
                  handleClick={() => (disableDeleteButton ? "" : setShowDeleteContactModal(true))}
                />
              </>
            )}
          </Segment>
        </div>
      )}
    </>
  );
};

export default ContactEllipsesButtonDropdown;
